<script lang="ts">
import { onBeforeMount, ref } from 'vue';

type AccordionItem = {
  id?: number;
  title: string;
  question?: string;
  content: string;
  answer?: string;
};

type Props = {
  initialOpenAccordionItems: Array<number | string>;
  itemCollection: AccordionItem[];
  multipleActive: boolean;
  itemIdPrefix?: string;
};

const CONTENT_PADDING = 16;
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<Props>(), {
  itemCollection: () => [],
  initialOpenAccordionItems: () => [],
  multipleActive: false,
});

const openItems = ref([...props.initialOpenAccordionItems]);

const reset = () => {
  openItems.value = [];
};

defineExpose({ reset });

const getIdentifier = (accordionItem: AccordionItem) =>
  accordionItem.id ? accordionItem.id : `accordion_${JSON.stringify(accordionItem)}`;

const toggleSingleMode = (identifier: number | string) => {
  openItems.value = openItems.value.includes(identifier) ? [] : [identifier];
};

const toggleMultipleMode = (identifier: number | string) => {
  if (openItems.value.includes(identifier)) {
    openItems.value = openItems.value.filter((item) => item !== identifier);
  } else {
    openItems.value.push(identifier);
  }
};

const setElementHeight = (el: Element, height: number) => {
  (el as HTMLElement).style.height = `${height}px`;
};

const openAccordion = (el: Element) => setElementHeight(el, el.scrollHeight + CONTENT_PADDING);

const closeAccordion = (el: Element) => setElementHeight(el, 0);

const appear = (el: Element) => {
  const timeOut = setTimeout(() => {
    openAccordion(el);
    clearTimeout(timeOut);
  }, 1000);
};

onBeforeMount(() => {
  if (props.initialOpenAccordionItems.length) {
    openItems.value = [...props.initialOpenAccordionItems];
  }
});
</script>

<template>
  <div :class="$style.container">
    <div
      v-for="accordionItem in itemCollection"
      :id="`${itemIdPrefix ?? ''}${accordionItem.id}`"
      :key="getIdentifier(accordionItem)"
      :class="$style.accordionItem"
    >
      <a
        :class="[
          $style.title,
          {
            [$style.active]: openItems.includes(getIdentifier(accordionItem)),
          },
        ]"
        @click.prevent="
          multipleActive
            ? toggleMultipleMode(getIdentifier(accordionItem))
            : toggleSingleMode(getIdentifier(accordionItem))
        "
        v-html="accordionItem.title || accordionItem.question"
      />
      <transition
        appear
        name="accordionToggle"
        @appear="appear"
        @before-appear="closeAccordion"
        @before-enter="closeAccordion"
        @before-leave="openAccordion"
        @enter="openAccordion"
        @leave="closeAccordion"
      >
        <div
          v-show="openItems.includes(getIdentifier(accordionItem))"
          :class="$style.content"
          v-html="accordionItem.content || accordionItem.answer"
        />
      </transition>
    </div>
  </div>
</template>

<style module lang="scss">
@forward './Accordion.scss';
</style>
